/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useLayoutEffect } from "react";
import '../../assets/styles/header.scss';
import User from '../../assets/images/user';
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { useNavigate, useLocation } from "react-router-dom";
import { commonGetApi, commonPostApi } from '../../utils/apiService';
import { API_ENDPOINTS } from '../../utils/apiEndpoints';
import { useDispatch, useSelector } from "react-redux";
import InputPrimaryComponents from "./inputPrimaryComponents";
import { useForm } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import { updateCamp, updateOrganization, userProfile } from "../../redux/commonSlice";
import socketService from '../../utils/socketService';

const Header = ({ onAvatarClick }) => {
  const overlayRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedMenu = useSelector((state) => state?.common?.selectedMenu);
  const userDetails = useSelector((state) => state?.common?.user);
  const [campaigns, setCampaigns] = useState([]);
  const [organization, setOrganization] = useState([]);
  const [notifications, setNotifications] = useState([]); // Add notifications state
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const dispatch = useDispatch();
  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = useForm();

  const isSettingsSection =
    location.pathname.startsWith("/users") ||
    location.pathname.startsWith("/camp") ||
    location.pathname.startsWith("/organizations") ||
    location.pathname.startsWith("/roles") ||
    location.pathname.startsWith("/donors") ||
    location.pathname.startsWith("/notifications/send") ||
    location.pathname.startsWith("/notifications/manage") ||
    location.pathname.startsWith("/notifications/history") ||
    location.pathname.startsWith("/systemsettings");

  const toggleOverlay = (e) => {
    if (overlayRef.current) {
      overlayRef.current.toggle(e); // Pass the event to toggle the overlay
    } else {
      console.error("OverlayRef is null");
    }
  };

  const markNotificationsSeen = () => {
    if (!notifications || notifications.length === 0) return;
    const top = notifications[0];
    const ts = new Date(top?.created_at || top?.createdAt || top?.updated_at || Date.now()).getTime();
    const topId = top?.value || top?.notification_id || top?.id;
    if (topId) localStorage.setItem("notification_id", String(topId));
    localStorage.setItem("notifications_last_seen_ts", String(ts || Date.now()));
    setHasNewNotification(false);
  };

  const getOrganizationCall = async () => {
    try {
      await commonGetApi(API_ENDPOINTS.GET_DASHBOARD_ORGANIZATION).then((res) => {
        let orgDetails = res?.data?.data?.map((item) => ({
          ...item,
          label: item?.organization_name,
          value: item?.organization_id,
        }));
        setOrganization(orgDetails);
        if (orgDetails?.[0]?.value) {
          setValue("organization", orgDetails?.[0]?.value);
          getCampCall(orgDetails?.[0]?.value);
        }
      });
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  // Add notification API call with better error handling and fallback data
  const getNotificationsCall = async () => {
    try {
      await commonPostApi(API_ENDPOINTS.GET_NOTIFICATION_BY_USER).then((res) => {
        let notificationDetails = res?.data?.data?.map((item) => ({
          ...item,
          label: item?.notification_description || "Notification",
          value: item?.notification_id || item?.id,
        }));


        // Sort newest first using created_at/updated_at if present, else by id/notification_id
        const getSortKey = (x) => {
          const ts = new Date(x?.created_at || x?.createdAt || x?.updated_at || 0).getTime();
          if (ts && !Number.isNaN(ts)) return ts;
          const num = Number(x?.notification_id || x?.id || 0);
          return Number.isNaN(num) ? 0 : num;
        };
        notificationDetails = (notificationDetails || []).sort((a, b) => getSortKey(b) - getSortKey(a));

        // Detect new: compare top item against last selected/seen id
        const topId = notificationDetails?.[0]?.value || notificationDetails?.[0]?.notification_id || notificationDetails?.[0]?.id;
        const lastSelectedId = localStorage.getItem("notification_id");
        if (topId && String(topId) !== String(lastSelectedId || "")) {
          setHasNewNotification(true);
        }

        setNotifications(notificationDetails);
        if (notificationDetails?.[0]?.value) {
          setValue("notification", notificationDetails?.[0]?.value);
        }
      });
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const getUserDetails = async () => {
    await commonGetApi(API_ENDPOINTS.USER_PROFILE).then((data) => {
      if (data?.data?.data) {
        dispatch(userProfile(data?.data?.data));
      }
    });
  };

  const getCampCall = async (org) => {
    try {
      await commonPostApi(API_ENDPOINTS.GET_DASHBOARD_CAMP, { organization_id: org }).then((res) => {
        let campDetails =
          res?.data?.data?.map((item) => ({
            ...item,
            label: item?.camp_name,
            value: item?.camp_details_id,
          })) || [];
        setCampaigns(campDetails);
        if (localStorage.getItem("camp_id")) {
          setValue("campaign", localStorage.getItem("camp_id"));
        } else {
          campDetails?.[0]?.value &&
            setValue("campaign", campDetails?.[0]?.value);
        }
      });
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  useLayoutEffect(() => {
    getOrganizationCall();
  }, []);

  useEffect(() => {
    if (userDetails?.user_id) {
      getNotificationsCall();
    }
  }, [userDetails?.user_id]);

  useEffect(() => {
    const handleNewMessage = (message) => {
      console.log("handleNewMessage==>", message);
      const socketNotification = {
        notification_id: message.notification_id,
        notification_description: message.notification_description,
        created_at: new Date().toISOString(),
        label: message.notification_title,
        value: message.notification_id,
      };
      setNotifications(prev => [socketNotification, ...prev]);
      setHasNewNotification(true);
    };

    socketService.on('newMessage', handleNewMessage);

    return () => {
      socketService.off('newMessage', handleNewMessage);
    };
  }, []);

  useEffect(() => {
    if (watch("organization")) {
      getCampCall(watch("organization"));
      localStorage.setItem("organization_id", watch("organization"));
      dispatch(
        updateOrganization(
          organization?.find(
            (item) => item?.organization_id === watch("organization")
          )
        )
      );
    }
  }, [watch("organization")]);

  useEffect(() => {
    if (watch("campaign")) {
      let camp = campaigns?.find(
        (item) => item?.camp_details_id === watch("campaign")
      );
      localStorage.setItem("camp_id", watch("campaign"));
      getUserDetails();
      dispatch(updateCamp(camp));
    }
  }, [watch("campaign")]);

  // Add notification change handler
  useEffect(() => {
    if (watch("notification")) {
      localStorage.setItem("notification_id", watch("notification"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("notification")]);

  return (
    <div className="Search__container">
      <DevTool control={control} />
      {!location.pathname.includes('/notifications') ? (
        <div className="Search__left-sec md:flex hidden">
          {selectedMenu?.icon && (
            <div className="icon w-20px text-center">
              {typeof selectedMenu?.icon === "string" ? (
                <i className={selectedMenu?.icon} style={{ fontSize: "22px" }}></i>
              ) : (
                selectedMenu?.icon
              )}
            </div>
          )}
          <span>{selectedMenu?.label}</span>
        </div>
      ) : (<div></div>)}

      <div className="Search__right-sec">
        <ul className="Search__action-items" >
          <div className="Camp_dropdown" onClick={markNotificationsSeen}>
            <InputPrimaryComponents
              key="notification-dropdown"
              inputParentClass={"div_pb-0"}
              type={"dropdown"}
              control={control}
              errors={errors}
              name={"notification"}
              rules={{ required: false }}
              options={notifications}
              placeholder="Select notification"
              valueTemplate={(value) => {
                if (!value) {
                  return (
                    <div className="flex align-items-center gap-2">
                      <div style={{ position: "relative", display: "inline-block" }}>
                        <i className="icon-bell text-2xl"></i>
                        {hasNewNotification && (
                          <span
                            style={{
                              position: "absolute",
                              top: 1,
                              right: -2,
                              width: 8,
                              height: 8,
                              background: "#FF3B30",
                              borderRadius: "50%",
                            }}
                          />
                        )}
                      </div>
                      <span className="md:flex hidden" style={{ color: "rgba(47, 43, 61, 0.9)" }}>Notifications</span>
                    </div>
                  );
                }
                return (
                  <div className="flex align-items-center gap-2">
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <i className="icon-bell text-2xl"></i>
                      {hasNewNotification && (
                        <span
                          style={{
                            position: "absolute",
                            top: -2,
                            right: -2,
                            width: 8,
                            height: 8,
                            background: "#FF3B30",
                            borderRadius: "50%",
                          }}
                        />
                      )}
                    </div>
                    <span className="md:flex hidden" style={{ color: "rgba(47, 43, 61, 0.9)" }}>
                      Notifications
                    </span>
                  </div>
                );
              }}
              itemTemplate={(option) => {
                return (
                  <div className="flex align-items-center gap-2 p-2">
                    <i className="icon-bell text-2xl"></i>
                    <span>{option?.label || "Notification"}</span>
                  </div>
                );
              }}
            />
          </div>
          {/*Hide the organization and camps dropdown while opening the settings page*/}
          {!isSettingsSection && (
            <div className="Camp_dropdown">
              <InputPrimaryComponents
                key="organization-dropdown"
                inputParentClass={"div_pb-0"}
                type={"dropdown"}
                control={control}
                errors={errors}
                name={"organization"}
                rules={{ required: "Select organization" }}
                options={organization}
                valueTemplate={(value) => {
                  return (
                    <div className="flex align-items-center gap-2 md:flex hidden">
                      {typeof value?.icon === "string" ? (
                        <i className={value?.icon}></i>
                      ) : value?.icon ? (
                        value?.icon
                      ) : (
                        <i className="icon-building text-2xl"></i>
                      )}
                      <span className="md:flex hidden">{value?.label}</span>
                    </div>
                  );
                }}
              />
            </div>
          )}

          {!(location.pathname.startsWith('/camp') || location.pathname.includes('reports/campManager')) &&
            !isSettingsSection && (
              <div className="Camp_dropdown ">
                <InputPrimaryComponents
                  key="campaign-dropdown"
                  inputParentClass={"div_pb-0"}
                  type={"dropdown"}
                  control={control}
                  errors={errors}
                  name={"campaign"}
                  rules={{ required: "Select campaign" }}
                  options={campaigns}
                  valueTemplate={(value) => {
                    return (
                      <div className="flex align-items-center gap-2">
                        {typeof value?.icon === "string" ? (
                          <i className={value?.icon}></i>
                        ) : value?.icon ? (
                          value?.icon
                        ) : (
                          <i className="icon-tent text-2xl"></i>
                        )}
                        <span className="md:flex hidden">{value?.label}</span>
                      </div>
                    );
                  }}
                />
              </div>
            )}

          <li className="ml-3">
            <img
              className="border-circle "
              src={userDetails?.profile_picture ? `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.SEARCH_IMAGE_BY_ID(userDetails?.user_id)}` : require('../../assets/images/no-picture.jpg')}
              alt=""
              onClick={toggleOverlay}
            />
          </li>
        </ul>
      </div>
      <Menu
        model={[
          {
            template: (
              <>
                <div className="Search__profile-container pr-4 pl-4 pb-3">
                  <div className="Search__profile-card">
                    <img
                      className="header-profile-image border-circle"
                      src={userDetails?.profile_picture ? `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.SEARCH_IMAGE_BY_ID(userDetails?.user_id)}` : require('../../assets/images/no-picture.jpg')}
                      alt="userProfile"
                    />
                    <div className="profile-info">
                      <p className="profile-name mt-0 mb-2">
                        {userDetails?.user_name}
                      </p>
                      <p className="profile-role m-0">{userDetails?.email}</p>
                    </div>
                  </div>
                </div>
                <div className="pr-4 pl-4" onClick={toggleOverlay}>
                  <div
                    className="cursor-pointer flex gap-2 align-items-center"
                    onClick={() => {
                      navigate("/profile");
                    }}
                  >
                    <User />
                    <p className="Search__secondary">My Profile</p>
                  </div>
                </div>
                <div className="Search__profile-container-end pr-4 pl-4 pt-3">
                  <Button
                    className="w-full font-medium text-base Search__bg-color"
                    label="Logout"
                    icon="pi pi-sign-out"
                    iconPos="right"
                    onClick={() => {
                      commonGetApi("/logout")
                        .then((data) => {
                          localStorage.clear();
                          window.location.href = "/login";
                        })
                        .catch((error) => {
                          console.error("Logout error:", error);
                          localStorage.clear();
                          window.location.href = "/login";
                        });
                    }}
                  />
                </div>
              </>
            ),
          },
        ]}
        popup
        ref={overlayRef}
        id="popup_menu_right"
        popupAlignment="right"
      />
    </div>
  );
};
export default Header;
